import {
  ClientContext,
  ClientProvider,
  useClient,
  useClientServices,
  useConfig,
  useContacts,
  useDevices,
  useDevtools,
  useHaloInvitation,
  useHaloInvitations,
  useIdentity,
  useInvitationStatus,
  useStatus
} from "./chunk-4CBWGYQ7.mjs";

// packages/sdk/react-client/src/index.ts
export * from "@dxos/client";

// packages/sdk/react-client/src/echo/observer.tsx
import { useEffect, useRef, useState } from "react";
var observer = (baseComponent) => {
  const baseComponentName = baseComponent.displayName || baseComponent.name;
  const observerComponent = (props) => {
    const client = useClient();
    const accessObserver = client.dbRouter.createAccessObserver();
    const [, setState] = useState([]);
    const forceUpdate = () => setState([]);
    const stateRef = useRef(null);
    if (!stateRef.current) {
      stateRef.current = {
        handle: null,
        timeout: null,
        mounted: false,
        updateBeforeMount: false
      };
    }
    const state = stateRef.current;
    if (!state.handle) {
      state.handle = client.dbRouter.createSubscription(() => {
        if (state.mounted) {
          forceUpdate();
        } else {
          state.updateBeforeMount = true;
        }
      });
      state.timeout = setTimeout(() => {
        var _a;
        (_a = state.handle) == null ? void 0 : _a.unsubscribe();
      }, 1e4);
    }
    useEffect(() => {
      state.mounted = true;
      if (state.timeout) {
        clearTimeout(state.timeout);
        state.timeout = null;
      }
      if (state.handle) {
        if (state.updateBeforeMount) {
          state.updateBeforeMount = false;
          forceUpdate();
        }
      } else {
        state.handle = client.dbRouter.createSubscription(() => {
          forceUpdate();
        });
        forceUpdate();
      }
      if (!state.handle.subscribed) {
        console.error("bug: subscription lost");
      }
      return () => {
        state.handle.unsubscribe();
        state.handle = null;
        state.mounted = false;
        state.updateBeforeMount = false;
      };
    }, []);
    try {
      return baseComponent(props);
    } finally {
      state.handle.update([
        ...accessObserver.accessed
      ]);
      accessObserver.pop();
    }
  };
  if (baseComponentName !== "") {
    observerComponent.displayName = baseComponentName;
  }
  return observerComponent;
};

// packages/sdk/react-client/src/echo/useMembers.ts
import { MulticastObservable } from "@dxos/async";
import { useMulticastObservable as useMulticastObservable2 } from "@dxos/react-async";

// packages/sdk/react-client/src/echo/useSpaces.ts
import { SpaceState } from "@dxos/client";
import { useMulticastObservable } from "@dxos/react-async";
var useSpace = (spaceKey) => {
  const spaces = useSpaces();
  return spaceKey ? spaces.find((space) => space.key.equals(spaceKey)) : void 0;
};
var useSpaces = ({ all = false } = {}) => {
  const client = useClient();
  const spaces = useMulticastObservable(client.spaces);
  return spaces.filter((space) => all || space.state.get() === SpaceState.READY);
};

// packages/sdk/react-client/src/echo/useMembers.ts
var useMembers = (spaceKey) => {
  var _a, _b;
  const spaces = useSpaces({
    all: true
  });
  const space = spaceKey ? spaces.find((space2) => space2.key.equals(spaceKey)) : void 0;
  const members = (_b = useMulticastObservable2((_a = space == null ? void 0 : space.members) != null ? _a : MulticastObservable.empty())) != null ? _b : [];
  return members;
};

// packages/sdk/react-client/src/echo/useQuery.ts
import { useMemo, useSyncExternalStore } from "react";
var useQuery = (space, filter, deps) => {
  var _a;
  const query = useMemo(() => space == null ? void 0 : space.db.query(filter != null ? filter : {}), [
    space == null ? void 0 : space.db,
    ...typeof filter === "function" ? [] : filterToDepsArray(filter),
    ...deps != null ? deps : []
  ]);
  return (_a = useSyncExternalStore((cb) => {
    var _a2, _b;
    return (_b = (_a2 = query == null ? void 0 : query.subscribe) == null ? void 0 : _a2.call(query, cb)) != null ? _b : cb;
  }, () => query == null ? void 0 : query.objects)) != null ? _a : [];
};
var filterToDepsArray = (filter) => Object.entries(filter != null ? filter : {}).flat();

// packages/sdk/react-client/src/echo/useSpaceInvitations.ts
import { useMemo as useMemo2, useSyncExternalStore as useSyncExternalStore2 } from "react";
import { SpaceProxy } from "@dxos/client";
var useSpaceInvitations = (spaceKey) => {
  var _a;
  const space = useSpace(spaceKey);
  const invitations = (_a = useSyncExternalStore2((listener) => {
    if (!(space instanceof SpaceProxy)) {
      return () => {
      };
    }
    const subscription = space.invitations.subscribe(() => listener());
    return () => subscription.unsubscribe();
  }, () => space == null ? void 0 : space.invitations.get())) != null ? _a : [];
  return invitations;
};
var useSpaceInvitation = (spaceKey, invitationId) => {
  const invitations = useSpaceInvitations(spaceKey);
  const invitation = useMemo2(() => invitations.find((invitation2) => invitation2.get().invitationId === invitationId), [
    invitations
  ]);
  return useInvitationStatus(invitation);
};

// packages/sdk/react-client/src/echo/useSubscription.ts
import { useEffect as useEffect2, useRef as useRef2 } from "react";
var useSubscription = (cb, selection) => {
  var _a;
  const client = useClient();
  const callbackRef = useRef2(cb);
  callbackRef.current = cb;
  const subscriptionRef = useRef2();
  useEffect2(() => {
    subscriptionRef.current = client.dbRouter.createSubscription(() => {
      callbackRef.current();
    });
    return () => {
      var _a2;
      return (_a2 = subscriptionRef.current) == null ? void 0 : _a2.unsubscribe();
    };
  }, []);
  (_a = subscriptionRef.current) == null ? void 0 : _a.update(selection);
  return subscriptionRef.current;
};

// packages/sdk/react-client/src/network/useNetworkStatus.ts
import { useSyncExternalStore as useSyncExternalStore3 } from "react";
var useNetworkStatus = () => {
  const client = useClient();
  const networkStatus = useSyncExternalStore3((listener) => {
    const subscription = client.mesh.networkStatus.subscribe(listener);
    return () => subscription.unsubscribe();
  }, () => client.mesh.networkStatus.get());
  return networkStatus;
};

// packages/sdk/react-client/src/util/useKeyStore.ts
import { useEffect as useEffect3, useMemo as useMemo3, useReducer } from "react";
var useKeyStore = (defaultKeys = []) => {
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const keyMap = useMemo3(() => /* @__PURE__ */ new Map(), []);
  useEffect3(() => {
    defaultKeys.forEach((key) => {
      const value = localStorage.getItem(key);
      keyMap.set(key, value != null ? value : "");
      if (value === void 0) {
        localStorage.setItem(key, "");
      }
    });
    forceUpdate();
  }, []);
  const setKey = (key, value) => {
    localStorage.setItem(key, value);
    keyMap.set(key, value);
    forceUpdate();
  };
  return [
    keyMap,
    setKey
  ];
};

// packages/sdk/react-client/src/util/useResultSet.ts
import { useMemo as useMemo4 } from "react";
import { useSubscription as useSubscription2 } from "use-subscription";
var useResultSet = (resultSet) => useSubscription2(useMemo4(() => ({
  getCurrentValue: () => resultSet.value,
  subscribe: (cb) => resultSet.subscribe(cb)
}), [
  resultSet
]));

// packages/sdk/react-client/src/util/useStream.ts
import { useState as useState2, useEffect as useEffect4 } from "react";
import { log } from "@dxos/log";
var useStream = (streamFactory, defaultValue, deps = []) => {
  const [value, setValue] = useState2(defaultValue);
  useEffect4(() => {
    const stream = streamFactory();
    stream.subscribe((response) => setValue(response), (err) => {
      if (err) {
        log.catch(err, {}, {
          file: "useStream.ts",
          line: 21,
          scope: void 0,
          callSite: (f, a) => f(...a)
        });
      }
    });
    return () => {
      stream.close();
    };
  }, deps);
  return value != null ? value : defaultValue;
};
export {
  ClientContext,
  ClientProvider,
  observer,
  useClient,
  useClientServices,
  useConfig,
  useContacts,
  useDevices,
  useDevtools,
  useHaloInvitation,
  useHaloInvitations,
  useIdentity,
  useInvitationStatus,
  useKeyStore,
  useMembers,
  useNetworkStatus,
  useQuery,
  useResultSet,
  useSpace,
  useSpaceInvitation,
  useSpaceInvitations,
  useSpaces,
  useStatus,
  useStream,
  useSubscription
};
//# sourceMappingURL=index.mjs.map
